// src/components/dashboard/ProfileView.tsx
import React, { useState } from 'react';
import { Mail, User as UserIcon, Shield, Copy, Check, Eye, EyeOff, FileText, ChevronDown, ChevronUp, Trash2, Fingerprint } from 'lucide-react';
import { TERMS_AND_CONDITIONS, PRIVACY_POLICY, QUICK_SUMMARY } from '../../legal/terms';

interface ProfileViewProps {
  userEmail: string;
  userId: string;
  healId?: string;
  onDeleteAccount?: () => Promise<void>;
}

export const ProfileView: React.FC<ProfileViewProps> = ({ userEmail, userId, healId, onDeleteAccount }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'terms' | 'privacy' | null>(null);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const jwtToken = localStorage.getItem('access_token') || 'No token found';

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const maskToken = (token: string) => {
    if (token === 'No token found') return token;
    return '•'.repeat(50);
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteAccountModal(true);
    setConfirmText('');
    setError('');
  };

  const handleConfirmDeleteAccount = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      setError('Please type DELETE MY ACCOUNT to confirm');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      if (onDeleteAccount) {
        await onDeleteAccount();
      }
      setShowDeleteAccountModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  return (
    <div style={{
      maxWidth: 'clamp(320px, 90vw, 900px)',
      margin: '0 auto',
      padding: 'clamp(var(--space-3), 4vw, var(--space-6))',
      fontFamily: 'var(--font-primary)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <h1 style={{
          fontSize: 'var(--text-4xl)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--color-primary)',
          marginBottom: 'var(--space-2)'
        }}>
          Your Profile
        </h1>
        <p style={{
          fontSize: 'var(--text-base)',
          color: 'var(--color-muted)',
          margin: 0
        }}>
          Manage your account information and security settings
        </p>
      </div>

      {/* Cards Container */}
      <div style={{
        display: 'grid',
        gap: 'var(--space-6)'
      }}>
        {/* Email Card */}
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'calc(var(--radius) * 2)',
          padding: 'var(--space-6)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          transition: 'var(--transition-base)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius)',
              backgroundColor: 'var(--color-accent-blue-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Mail size={24} color="var(--color-accent-blue)" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-muted)',
                margin: '0 0 var(--space-2) 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Email Address
              </h3>
              <p style={{
                fontSize: 'var(--text-lg)',
                color: 'var(--color-primary)',
                margin: '0 0 var(--space-1) 0',
                fontWeight: 'var(--font-medium)',
                wordBreak: 'break-word'
              }}>
                {userEmail}
              </p>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-muted)',
                margin: 0
              }}>
                Your primary contact email
              </p>
            </div>
            <button
              onClick={() => handleCopy(userEmail, 'email')}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: copiedField === 'email' ? 'var(--color-hover-bg)' : 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                color: copiedField === 'email' ? 'var(--color-success)' : 'var(--color-secondary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                transition: 'var(--transition-fast)',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (copiedField !== 'email') {
                  e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                if (copiedField !== 'email') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {copiedField === 'email' ? (
                <>
                  <Check size={16} />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={16} />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Heal ID Card */}
        {healId && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'calc(var(--radius) * 2)',
            padding: 'var(--space-6)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'var(--transition-base)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius)',
                backgroundColor: 'var(--color-accent-green-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Shield size={24} color="var(--color-accent-green)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-2) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Heal ID
                </h3>
                <p style={{
                  fontSize: 'var(--text-lg)',
                  color: 'var(--color-primary)',
                  margin: '0 0 var(--space-1) 0',
                  fontWeight: 'var(--font-medium)',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace'
                }}>
                  {healId}
                </p>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-muted)',
                  margin: 0
                }}>
                  Your secure patient identifier
                </p>
              </div>
              <button
                onClick={() => handleCopy(healId, 'healId')}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  backgroundColor: copiedField === 'healId' ? 'var(--color-hover-bg)' : 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  color: copiedField === 'healId' ? 'var(--color-success)' : 'var(--color-secondary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  transition: 'var(--transition-fast)',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (copiedField !== 'healId') {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (copiedField !== 'healId') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {copiedField === 'healId' ? (
                  <>
                    <Check size={16} />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* User ID Card - Only shown in debug/development mode */}
        {import.meta.env.VITE_ENABLE_DEBUG === 'true' && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'calc(var(--radius) * 2)',
            padding: 'var(--space-6)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'var(--transition-base)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius)',
                backgroundColor: 'var(--color-accent-purple-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <UserIcon size={24} color="var(--color-accent-purple)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-2) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Account ID
                </h3>
                <p style={{
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-primary)',
                  margin: '0 0 var(--space-1) 0',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  letterSpacing: '-0.02em'
                }}>
                  {userId}
                </p>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-muted)',
                  margin: 0
                }}>
                  Your unique identifier
                </p>
              </div>
              <button
                onClick={() => handleCopy(userId, 'userId')}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  backgroundColor: copiedField === 'userId' ? 'var(--color-hover-bg)' : 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  color: copiedField === 'userId' ? 'var(--color-success)' : 'var(--color-secondary)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  transition: 'var(--transition-fast)',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (copiedField !== 'userId') {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (copiedField !== 'userId') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {copiedField === 'userId' ? (
                  <>
                    <Check size={16} />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Security Token Card - Only shown in debug/development mode */}
        {import.meta.env.VITE_ENABLE_DEBUG === 'true' && (
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'calc(var(--radius) * 2)',
            padding: 'var(--space-6)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'var(--transition-base)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: 'var(--radius)',
                backgroundColor: 'var(--color-accent-red-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Shield size={24} color="var(--color-accent-red)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--color-muted)',
                  margin: '0 0 var(--space-2) 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Security Token
                </h3>
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-primary)',
                  margin: '0 0 var(--space-1) 0',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--color-background)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--color-border)',
                  maxHeight: '100px',
                  overflowY: 'auto',
                  lineHeight: '1.6',
                  letterSpacing: '-0.01em'
                }}>
                  {showToken ? jwtToken : maskToken(jwtToken)}
                </div>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-muted)',
                  margin: 0
                }}>
                  Your authentication token for API access
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <button
                  onClick={() => setShowToken(!showToken)}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    color: 'var(--color-secondary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    transition: 'var(--transition-fast)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {showToken ? (
                    <>
                      <EyeOff size={16} />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <Eye size={16} />
                      <span>Show</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleCopy(jwtToken, 'token')}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    backgroundColor: copiedField === 'token' ? 'var(--color-hover-bg)' : 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    color: copiedField === 'token' ? 'var(--color-success)' : 'var(--color-secondary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    transition: 'var(--transition-fast)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (copiedField !== 'token') {
                      e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (copiedField !== 'token') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {copiedField === 'token' ? (
                    <>
                      <Check size={16} />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Notice - Only shown in debug/development mode */}
      {import.meta.env.VITE_ENABLE_DEBUG === 'true' && (
        <div style={{
          marginTop: 'var(--space-6)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'flex-start'
        }}>
          <Shield size={20} color="var(--color-accent-red)" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)',
              color: 'var(--color-accent-red)',
              margin: '0 0 var(--space-1) 0'
            }}>
              Keep your token secure
            </p>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-muted)',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Never share your security token with anyone. It provides full access to your account.
            </p>
          </div>
        </div>
      )}

      {/* Terms & Conditions and Privacy Policy Section */}
      <div style={{
        marginTop: 'var(--space-8)',
        borderTop: '2px solid var(--color-border)',
        paddingTop: 'var(--space-8)'
      }}>
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h2 style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-primary)',
            marginBottom: 'var(--space-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <FileText size={24} />
            Legal Documents
          </h2>
          <p style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-muted)',
            margin: 0
          }}>
            Review our Terms & Conditions and Privacy Policy
          </p>
        </div>

        {/* Quick Summary Banner */}
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          marginBottom: 'var(--space-4)'
        }}>
          <h3 style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-bold)',
            color: '#1e40af',
            margin: '0 0 var(--space-2) 0'
          }}>
            Quick Summary
          </h3>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: '#1e3a8a',
            margin: 0,
            lineHeight: '1.6',
            whiteSpace: 'pre-line'
          }}>
            {QUICK_SUMMARY}
          </p>
        </div>

        {/* Terms & Conditions Card */}
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'calc(var(--radius) * 2)',
          padding: 'var(--space-6)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          marginBottom: 'var(--space-4)'
        }}>
          <button
            onClick={() => setExpandedSection(expandedSection === 'terms' ? null : 'terms')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginBottom: expandedSection === 'terms' ? 'var(--space-4)' : 0
            }}
          >
            <div>
              <h3 style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--color-primary)',
                margin: '0 0 var(--space-1) 0',
                textAlign: 'left'
              }}>
                Terms & Conditions
              </h3>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-muted)',
                margin: 0,
                textAlign: 'left'
              }}>
                Last updated: {TERMS_AND_CONDITIONS.lastUpdated}
              </p>
            </div>
            {expandedSection === 'terms' ? (
              <ChevronUp size={24} color="var(--color-primary)" />
            ) : (
              <ChevronDown size={24} color="var(--color-muted)" />
            )}
          </button>

          {expandedSection === 'terms' && (
            <div style={{
              maxHeight: '500px',
              overflowY: 'auto',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)'
            }}>
              {TERMS_AND_CONDITIONS.sections.map((section, index) => (
                <div key={index} style={{ marginBottom: 'var(--space-6)' }}>
                  <h4 style={{
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-primary)',
                    margin: '0 0 var(--space-2) 0'
                  }}>
                    {section.title}
                  </h4>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-secondary)',
                    margin: 0,
                    lineHeight: '1.7',
                    whiteSpace: 'pre-line'
                  }}>
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Privacy Policy Card */}
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'calc(var(--radius) * 2)',
          padding: 'var(--space-6)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <button
            onClick={() => setExpandedSection(expandedSection === 'privacy' ? null : 'privacy')}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginBottom: expandedSection === 'privacy' ? 'var(--space-4)' : 0
            }}
          >
            <div>
              <h3 style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--color-primary)',
                margin: '0 0 var(--space-1) 0',
                textAlign: 'left'
              }}>
                Privacy Policy
              </h3>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--color-muted)',
                margin: 0,
                textAlign: 'left'
              }}>
                Last updated: {PRIVACY_POLICY.lastUpdated}
              </p>
            </div>
            {expandedSection === 'privacy' ? (
              <ChevronUp size={24} color="var(--color-primary)" />
            ) : (
              <ChevronDown size={24} color="var(--color-muted)" />
            )}
          </button>

          {expandedSection === 'privacy' && (
            <div style={{
              maxHeight: '500px',
              overflowY: 'auto',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)'
            }}>
              {PRIVACY_POLICY.sections.map((section, index) => (
                <div key={index} style={{ marginBottom: 'var(--space-6)' }}>
                  <h4 style={{
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-primary)',
                    margin: '0 0 var(--space-2) 0'
                  }}>
                    {section.title}
                  </h4>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-secondary)',
                    margin: 0,
                    lineHeight: '1.7',
                    whiteSpace: 'pre-line'
                  }}>
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone - Delete Account */}
      <div style={{
        marginTop: 'var(--space-8)',
        borderTop: '2px solid var(--color-border)',
        paddingTop: 'var(--space-8)'
      }}>
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h2 style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-bold)',
            color: '#DC2626',
            marginBottom: 'var(--space-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <Trash2 size={24} />
            Danger Zone
          </h2>
          <p style={{
            fontSize: 'var(--text-base)',
            color: 'var(--color-muted)',
            margin: 0
          }}>
            Permanent actions that cannot be undone
          </p>
        </div>

        {/* Delete Account Card */}
        <div style={{
          backgroundColor: '#FEE2E2',
          borderRadius: 'calc(var(--radius) * 2)',
          padding: 'var(--space-6)',
          border: '2px solid #DC2626',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: 'var(--radius)',
              backgroundColor: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Trash2 size={24} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-bold)',
                color: '#991B1B',
                margin: '0 0 var(--space-2) 0'
              }}>
                Delete Account Permanently
              </h3>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: '#991B1B',
                margin: 0,
                lineHeight: '1.6',
                marginBottom: 'var(--space-2)'
              }}>
                This will permanently delete your account and ALL associated data including health reports, metrics, insights, chat conversations, and profile information. You will NOT be able to log in again.
              </p>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: '#991B1B',
                margin: 0,
                fontWeight: 'var(--font-bold)'
              }}>
                ⚠️ This action is IRREVERSIBLE and cannot be undone.
              </p>
            </div>
          </div>
          <button
            onClick={handleDeleteAccountClick}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: '#DC2626',
              border: 'none',
              borderRadius: 'var(--radius)',
              color: 'white',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-bold)',
              cursor: 'pointer',
              transition: 'var(--transition-fast)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#B91C1C';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#DC2626';
            }}
          >
            <Trash2 size={16} />
            Delete My Account Forever
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontFamily: 'var(--font-primary)'
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius)',
            padding: 'clamp(var(--space-4), 4vw, var(--space-6))',
            maxWidth: 'min(550px, 95vw)',
            width: '90%',
            boxShadow: 'var(--shadow-2xl)',
            border: '3px solid #DC2626',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Warning Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)'
            }}>
              <div style={{
                backgroundColor: '#DC2626',
                borderRadius: '50%',
                padding: 'var(--space-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Trash2 size={28} color="white" />
              </div>
              <h2 style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-bold)',
                color: '#DC2626',
                margin: 0
              }}>
                Delete Account Forever?
              </h2>
            </div>

            {/* Critical Warning Message */}
            <div style={{
              backgroundColor: '#7F1D1D',
              border: '2px solid #DC2626',
              borderRadius: 'var(--radius)',
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-4)'
            }}>
              <p style={{
                fontSize: 'var(--text-base)',
                color: 'white',
                margin: 0,
                marginBottom: 'var(--space-3)',
                fontWeight: 'var(--font-bold)',
                textAlign: 'center'
              }}>
                🚨 CRITICAL WARNING 🚨
              </p>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'white',
                margin: 0,
                lineHeight: 1.6,
                marginBottom: 'var(--space-2)'
              }}>
                This will permanently delete:
              </p>
              <ul style={{
                fontSize: 'var(--text-sm)',
                color: 'white',
                margin: 0,
                paddingLeft: 'var(--space-5)',
                lineHeight: 1.8
              }}>
                <li>Your entire account (you cannot log in again)</li>
                <li>All health reports and metrics</li>
                <li>All AI insights and analysis</li>
                <li>All chat conversations</li>
                <li>Your health profile and data</li>
              </ul>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'white',
                margin: 'var(--space-3) 0 0 0',
                fontWeight: 'var(--font-bold)',
                textAlign: 'center'
              }}>
                THIS ACTION CANNOT BE REVERSED OR UNDONE
              </p>
            </div>

            {/* Confirmation Input */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Type <span style={{ color: '#DC2626', fontSize: 'var(--text-base)' }}>DELETE MY ACCOUNT</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE MY ACCOUNT here"
                disabled={isDeleting}
                autoFocus
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: `2px solid ${error ? '#DC2626' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!error) e.currentTarget.style.borderColor = '#DC2626';
                }}
                onBlur={(e) => {
                  if (!error) e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              />
              {error && (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: '#DC2626',
                  margin: 'var(--space-2) 0 0 0',
                  fontWeight: 'var(--font-medium)'
                }}>
                  {error}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setConfirmText('');
                  setError('');
                }}
                disabled={isDeleting}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-bold)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: '2px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  opacity: isDeleting ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-background)';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteAccount}
                disabled={isDeleting || confirmText !== 'DELETE MY ACCOUNT'}
                style={{
                  padding: 'var(--space-3) var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-bold)',
                  fontFamily: 'var(--font-primary)',
                  color: 'white',
                  backgroundColor: '#DC2626',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  cursor: (isDeleting || confirmText !== 'DELETE MY ACCOUNT') ? 'not-allowed' : 'pointer',
                  opacity: (isDeleting || confirmText !== 'DELETE MY ACCOUNT') ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting && confirmText === 'DELETE MY ACCOUNT') {
                    e.currentTarget.style.backgroundColor = '#991B1B';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting && confirmText === 'DELETE MY ACCOUNT') {
                    e.currentTarget.style.backgroundColor = '#DC2626';
                  }
                }}
              >
                {isDeleting ? (
                  <>
                    <span>Deleting Account...</span>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Account Forever
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
